import React, { useEffect, useRef, useState, useMemo } from "react";
import Chip from "@mui/material/Chip";
import TextField from "@mui/material/TextField";
import styles from "./ConsentDetails.module.scss";
import IconButton from "@mui/material/IconButton";
import DeleteIcon from "@mui/icons-material/Delete";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Box from "@mui/material/Box";
import {
  Modal,
  Button,
  FormControl,
  FormLabel,
  RadioGroup,
  Radio,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
} from "@mui/material";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import parse from "html-react-parser";
import SignatureCanvas from "react-signature-canvas";
import logo from "../assets/logo.png";
import ReactPlayer from "react-player";
import SkipNextIcon from "@mui/icons-material/SkipNext";
import { toast } from "react-toastify";
import signatureImage from "./signature.jpg";
import draftToHtml from "draftjs-to-html";
import { EditorState, convertToRaw } from "draft-js";
import CircularProgress from "@material-ui/core/CircularProgress";
import ReportIcon from "@mui/icons-material/Report";
import useMediaQuery from "@mui/material/useMediaQuery";
import Authenticate from "./Authenticate";

const steps = ["Consent Details", "Consent form", "Fp17DC form"];

type SavedSignature = {
  image: string | null;
  name: string | null;
  signed_party: "patient" | "parent" | "guardian";
  guardianName?: string | null;
  created_at: number;
};

type SignatureMeta = {
  image: string | null;
  name: string | null;
  timestamp: number;
};

type ClickedButton = {
  id: string;
  signature: SignatureMeta;
};

const SIGNATURE_STORAGE_PREFIX = "consent_saved_signature_v1";

export default function ConsentDetails() {
  const { consentId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [activeStep, setActiveStep] = useState(0);
  const [openAuthenticate, setOpenAuthenticate] = useState(true);
  const [checked, setChecked] = useState(false);
  const [isFormSuccess, setIsFormSuccess] = useState(false);
  const [consentBody, setConsentBody] = useState("");
  const [hasPlaceholder, sethasPlaceholder] = useState(false);
  const [isMandatory, setIsMandatory] = useState(false);
  const [mandatoryButtonIds, setMandatoryButtonIds] = useState<string[]>([]);
  const [clickedButtonId, setClickedButtonId] = useState<string | null>(null);
  const [signtureUpdates, setSigntureUpdates] = useState<ClickedButton[]>([]);
  const [showBanner, setShowBanner] = useState(false);
  const [buttonColors, setButtonColors] = useState<Record<string, string>>({});
  const [savedSignature, setSavedSignature] = useState<SavedSignature | null>(
    null
  );
  const savedSignatureRef = useRef<SavedSignature | null>(null);
  useEffect(() => {
    savedSignatureRef.current = savedSignature;
  }, [savedSignature]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeInput, setActiveInput] = useState<"signature" | "name">(
    "signature"
  );
  const [userFullName, setUserFullName] = useState("");
  const [isSignatureCaptured, setIsSignatureCaptured] = useState(false);
  const [isImageVisible, setIsImageVisible] = useState(true);

  const [consentType, setConsentType] = useState<
    "patient" | "parent" | "guardian"
  >("patient");
  const [guardianName, setGuardianName] = useState<string>("");

  const mySignature = useRef<SignatureCanvas | null>(null);
  const isMobile = useMediaQuery("(max-width:767px)");

  // Video state
  const [playlistisnull, setPlaylistisnull] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState<any>(null);
  const [selectedVideoPlayListIndex, setSelectedVideoPlayListIndex] =
    useState(0);
  const [selectedVideoIndex, setSelectedVideoIndex] = useState(0);
  const [currentVideoTime, setCurrentVideoTime] = useState(0);
  const [expandedPanel, setExpandedPanel] = useState<number | null>(null);
  const initializedRef = useRef(false);
  const playerRef = useRef<any>(null);

  const [captionLang, setCaptionLang] = useState("en");
  const [remountKey, setRemountKey] = useState<string>("init");
  const seekAfterReloadRef = useRef<number | null>(null);
  const wasPlayingRef = useRef<boolean>(false);
  const ytRef = useRef<any>(null);

  const [requirefp17dc, setRequirefp17dc] = useState(false);
  const [pdfUrl, setPdfUrl] = useState("");
  const [isLoadingViewPdf, setIsLoadingViewPdf] = useState(false);
  const [nhsChecked, setNhsChecked] = useState(false);
  const [prvChecked, setPrvChecked] = useState(false);
  const [isNhsTreatmentSigned, setIsNhsTreatmentSigned] = useState(false);

  const [editorState] = useState<any>(EditorState.createEmpty());
  const [patientTime, setPatientTime] = useState({ read: 0, watch: 0 });

  // ----------------------------- Persist signature per consentId -----------------------------
  const signatureStorageKey = `${SIGNATURE_STORAGE_PREFIX}:${
    consentId || "unknown"
  }`;

  const loadSavedSignature = () => {
    try {
      const raw = localStorage.getItem(signatureStorageKey);
      if (!raw) return null;
      const parsed = JSON.parse(raw) as SavedSignature;
      if (!parsed?.created_at) return null;
      return parsed;
    } catch {
      return null;
    }
  };

  const persistSavedSignature = (sig: SavedSignature | null) => {
    try {
      if (!sig) {
        localStorage.removeItem(signatureStorageKey);
        return;
      }
      localStorage.setItem(signatureStorageKey, JSON.stringify(sig));
    } catch {}
  };

  useEffect(() => {
    const s = loadSavedSignature();
    if (s) setSavedSignature(s);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [consentId]);

  useEffect(() => {
    persistSavedSignature(savedSignature);
  }, [savedSignature]);

  // ----------------------------- Helpers -----------------------------
  const eraseSignature = () => {
    mySignature.current?.clear();
    setIsSignatureCaptured(false);
    setIsImageVisible(true);
  };

  const openSignatureModal = (targetButtonId: string | null) => {
    setClickedButtonId(targetButtonId);
    setIsModalOpen(true);
    setActiveInput("signature");
    setIsSignatureCaptured(false);
    setIsImageVisible(true);
    setUserFullName("");
    eraseSignature();
  };

  const closeSignatureModal = () => {
    setIsModalOpen(false);
    setClickedButtonId(null);
    setActiveInput("signature");
    setUserFullName("");
    eraseSignature();
  };

 const applySignatureToButtonUI = (buttonId: string, mandatory: boolean) => {
  const btn = document.getElementById(buttonId) as HTMLButtonElement | null;
  if (btn) {
    if (mandatory) {
      btn.innerHTML = `Signature Applied <span style="color: red;">*</span>`;
    } else {
      btn.innerText = "Signature Applied";
    }

    const color = mandatory ? "green" : "green"; 

   
    btn.style.setProperty("background", color, "important");
    btn.style.setProperty("background-color", color, "important");
    btn.style.setProperty("color", "white", "important");
    btn.style.setProperty("border", "none", "important");
  }

  const unsignedBtn = document.getElementById(`unsigned-${buttonId}`);
  if (unsignedBtn) unsignedBtn.style.display = "inline-block";
};

  const resetSignatureButtonUI = (buttonId: string) => {
  const btn = document.getElementById(buttonId) as HTMLButtonElement | null;
  if (btn) {
    const isMandatoryBtn = buttonId.includes("mandatory");
    btn.innerText = isMandatoryBtn
      ? "Apply Signature Here *"
      : "Apply Signature Here (Optional)";

    btn.style.backgroundColor = isMandatoryBtn ? "red" : "";
    btn.style.color = isMandatoryBtn ? "white" : "";
    btn.style.border = "";
  }

  const unsignedBtn = document.getElementById(`unsigned-${buttonId}`);
  if (unsignedBtn) unsignedBtn.style.display = "none";
};

  const upsertSignatureUpdate = (buttonId: string, signature: SignatureMeta) => {
    setSigntureUpdates((prev) => {
      const idx = prev.findIndex((x) => x.id === buttonId);
      if (idx !== -1) {
        const copy = prev.slice();
        copy[idx] = { id: buttonId, signature };
        return copy;
      }
      return [...prev, { id: buttonId, signature }];
    });
    setButtonColors((prev) => ({ ...prev, [buttonId]: "green" }));
  };

  const applySavedSignatureToPlaceholder = (buttonId: string) => {
    const sig = savedSignatureRef.current;
    if (!sig) return false;

    const meta: SignatureMeta = {
      image: sig.image,
      name:
        sig.signed_party === "parent" || sig.signed_party === "guardian"
          ? sig.guardianName || sig.name || null
          : sig.name || null,
      timestamp: Date.now(),
    };

    upsertSignatureUpdate(buttonId, meta);

    const mandatory = buttonId.includes("mandatory");
    applySignatureToButtonUI(buttonId, mandatory);

    return true;
  };

  
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target) return;

      // remove signature
      const unsigned = target.closest(".unsigned-btn") as HTMLElement | null;
      if (unsigned) {
        e.preventDefault();
        const buttonId = unsigned.id.replace("unsigned-", "");
        setSigntureUpdates((prev) => prev.filter((x) => x.id !== buttonId));
        resetSignatureButtonUI(buttonId);
        return;
      }

      // signature placeholder buttons
      const sigBtn = target.closest(
        "button.mandatory-signature-btn, button.optional-signature-btn"
      ) as HTMLButtonElement | null;

      if (!sigBtn) return;

      e.preventDefault();
      const id = sigBtn.id;
      if (!id) return;

      // If signature already saved -> apply instantly
      if (savedSignatureRef.current) {
        applySavedSignatureToPlaceholder(id);
        return;
      }

      // First time -> open modal
      openSignatureModal(id);
    };

    document.addEventListener("click", handler);
    return () => document.removeEventListener("click", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Apply button colors after parse re-renders
  useEffect(() => {
    const buttons = document.querySelectorAll("button");
    buttons.forEach((button) => {
      const buttonId = (button as HTMLButtonElement).id;
      if (buttonId && buttonColors[buttonId]) {
        (button as HTMLButtonElement).style.backgroundColor =
          buttonColors[buttonId];
      }
    });
  }, [buttonColors, consentBody]);

  // ----------------------------- Consent builders -----------------------------
  const formatDob = (dob?: string | null) => {
    if (!dob) return "—";
    const d = new Date(dob);
    if (Number.isNaN(d.getTime())) return "—";
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yyyy = d.getFullYear();
    return `${dd}/${mm}/${yyyy}`;
  };

  const buildPatientAddress = (p: any) => {
    if (!p) return "—";
    const parts = [
      p.address_line_1,
      p.address_line_2,
      p.address_line_3,
      p.town,
      p.county,
      p.postcode,
    ].filter(Boolean);
    return parts.length ? parts.join(", ") : "—";
  };

  const formatMoney = (n: any) => {
    const num = Number(n);
    if (Number.isNaN(num)) return "—";
    return `£${num.toFixed(2)}`;
  };

  const buildPatientHeaderHTML = (data: any) => {
  const p = data?.patient;
  const fullName = [p?.title, p?.first_name, p?.middle_name, p?.last_name]
    .filter(Boolean)
    .join(" ");
  const dob = formatDob(p?.dob);
  const address = buildPatientAddress(p);
  const readMins = data?.patient_time?.read_time_mins ?? 0;
  const watchMins = data?.patient_time?.watch_time_mins ?? 0;

  return `
    <div style="border:1px solid #e5e7eb;border-radius:12px;padding:16px;margin-bottom:16px;background:#fafafa;">
      <div style="display:flex;flex-direction:column;gap:6px;font-size:14px;color:#111827;">
        <div><strong>Patient:</strong> ${fullName || "—"}</div>
        <div><strong>D.O.B:</strong> ${dob}</div>
        <div><strong>Address:</strong> ${address}</div>

        <div style="display:flex;flex-wrap:wrap;gap:10px;margin-top:10px;">
          <div style="padding:10px 12px;border-radius:10px;background:#ecfdf5;border:1px solid #a7f3d0;color:#065f46;font-weight:700;">
            Estimated Reading Time: ${readMins} minutes
          </div>

          <div style="padding:10px 12px;border-radius:10px;background:#eef2ff;border:1px solid #c7d2fe;color:#3730a3;font-weight:700;">
            Estimated Watch Time: ${watchMins} minutes
          </div>
        </div>
      </div>
    </div>
  `;
};

  const buildTreatmentPlanHTML = (data: any) => {
    const treatments = data?.treatment_plan?.treatments || [];
    if (!treatments.length) {
      return `
        <div style="margin:16px 0;padding:12px;border:1px dashed #d1d5db;border-radius:12px;">
          <strong>Pricing and Treatment Plan</strong>
          <div style="margin-top:6px;color:#6b7280;">No treatments listed.</div>
        </div>
      `;
    }

    const total = treatments.reduce((sum: number, t: any) => {
      const n = Number(t?.price);
      return sum + (Number.isFinite(n) ? n : 0);
    }, 0);

    const rows = treatments
      .map((t: any) => {
        return `
          <tr>
            <td style="padding:10px;border-bottom:1px solid #eee;">${
              t?.nomenclature ?? "—"
            }</td>
            <td style="padding:10px;border-bottom:1px solid #eee;text-align:right;white-space:nowrap;">
              ${formatMoney(t?.price)}
            </td>
          </tr>
        `;
      })
      .join("");

    const totalRow = `
      <tr>
        <td style="padding:12px 10px;font-weight:800;border-top:2px solid #e5e7eb;">TOTAL</td>
        <td style="padding:12px 10px;font-weight:800;text-align:right;white-space:nowrap;border-top:2px solid #e5e7eb;">
          ${formatMoney(total)}
        </td>
      </tr>
    `;

    return `
      <div style="margin:16px 0;border:1px solid #e5e7eb;border-radius:12px;overflow:hidden;">
        <div style="padding:12px 14px;background:#111827;color:white;font-weight:600;">
          Pricing and Treatment Plan
        </div>
        <table style="width:100%;border-collapse:collapse;font-size:14px;">
          <thead>
            <tr style="background:#f9fafb;">
              <th style="text-align:left;padding:10px;">Treatment</th>
              <th style="text-align:right;padding:10px;">Price</th>
            </tr>
          </thead>
          <tbody>${rows}${totalRow}</tbody>
        </table>
      </div>
    `;
  };

  // ----------------------------- Query: treatmentDetails -----------------------------
  const treatmentDetails = useQuery(
    ["treatmentDetails", consentId],
    async () => {
      const { data } = await axios.get(`/patient/consent_bundle/${consentId}`);
      return data;
    },
    {
      enabled: !openAuthenticate,
      refetchOnWindowFocus: false,
      onSuccess: (data) => {
        setRequirefp17dc(!!data.require_fp17dc);

        if (data?.patient_time) {
          setPatientTime({
            read: data.patient_time.read_time_mins || 0,
            watch: data.patient_time.watch_time_mins || 0,
          });
        }

        let isPlaylistNull = false;
        if (!data.playlists || data.playlists.length === 0) isPlaylistNull = true;
        else isPlaylistNull = data.playlists.every((pl: any) => pl.status === false);
        setPlaylistisnull(isPlaylistNull);

        // ✅ Auto-pick first active playlist + first active video (this is key)
        if (!isPlaylistNull) {
          const firstActivePlaylistIndex = data.playlists.findIndex((pl: any) => pl?.status);
          if (firstActivePlaylistIndex > -1) {
            const firstActiveVideoIndex = data.playlists[firstActivePlaylistIndex]?.playlist_items?.findIndex(
              (v: any) => v?.status
            );
            if (firstActiveVideoIndex > -1) {
              setSelectedVideoPlayListIndex(firstActivePlaylistIndex);
              setSelectedVideoIndex(firstActiveVideoIndex);
              setSelectedVideo(data.playlists[firstActivePlaylistIndex].playlist_items[firstActiveVideoIndex]);
            }
          }
        }

        if (data?.consent_form?.clause_blocks?.length) {
          const sortedBlocks = data.consent_form.clause_blocks
            .slice()
            .sort((a: any, b: any) => a.order - b.order);

          const introBlocks =
            sortedBlocks.length > 1 ? sortedBlocks.slice(0, -1) : sortedBlocks;
          const lastBlock =
            sortedBlocks.length > 1
              ? sortedBlocks[sortedBlocks.length - 1]
              : null;

          const patientHeaderHTML = buildPatientHeaderHTML(data);
          const treatmentPlanHTML = buildTreatmentPlanHTML(data);

          const introHTML = introBlocks.map((b: any) => b.content || "").join("<hr />");
          const outroHTML = lastBlock ? lastBlock.content || "" : "";

          let modifiedBody = `
            ${patientHeaderHTML}
            ${introHTML}
            ${treatmentPlanHTML}
            ${outroHTML ? "<hr />" + outroHTML : ""}
          `;

          modifiedBody = cleanHtmlSpacing(modifiedBody);

          let buttonCount = 1;
          let hasPlaceholdersLocal = false;

          setMandatoryButtonIds([]);
          setIsMandatory(false);

          modifiedBody = modifiedBody.replace(
            /\[(signature_(optional|mandatory))\]/g,
            (_match: any, _fullType: any, type: "optional" | "mandatory") => {
              hasPlaceholdersLocal = true;

              const buttonId = `${type}-signature-btn-${buttonCount}`;
              const isMandatoryBtn = type === "mandatory";

              if (isMandatoryBtn) {
                setIsMandatory(true);
                setMandatoryButtonIds((prev) => [...prev, buttonId]);
              }

              const buttonHTML = `
                <button id="${buttonId}" class="${type}-signature-btn"
                  style="${isMandatoryBtn ? "background:red;color:white;" : ""}">
                  ${
                    isMandatoryBtn
                      ? "Apply Signature Here *"
                      : "Apply Signature Here (Optional)"
                  }
                </button>
                <button id="unsigned-${buttonId}" class="unsigned-btn"
                  title="Remove Signature" style="display:none;">✕</button>
              `;

              buttonCount++;
              return buttonHTML;
            }
          );

          modifiedBody = cleanHtmlSpacing(modifiedBody);

          if (!hasPlaceholdersLocal) sethasPlaceholder(true);
          else sethasPlaceholder(false);

          setConsentBody(modifiedBody);
        }
      },
    }
  );

  // ----------------------------- Videos helpers -----------------------------
  const hasActiveVideos = useMemo(() => {
    return !!treatmentDetails?.data?.playlists?.some(
      (pl: any) =>
        pl?.status &&
        Array.isArray(pl?.playlist_items) &&
        pl.playlist_items.some((v: any) => v?.status)
    );
  }, [treatmentDetails?.data]);

  useEffect(() => {
    if (!initializedRef.current && treatmentDetails?.data?.playlists?.length) {
      const firstActiveIndex = treatmentDetails.data.playlists.findIndex(
        (p: any) => p.status
      );
      if (firstActiveIndex !== -1) {
        setExpandedPanel(firstActiveIndex);
        initializedRef.current = true;
      }
    }
  }, [treatmentDetails]);

  const addVideoLog = useMutation(
    async (data: { type: string; timeStamp: number; videoId: string | undefined }) => {
      if (!data.videoId) return null;
      return axios.post(
        `/consent_bundle/${treatmentDetails.data.id}/video/${data.videoId}/log`,
        data
      );
    }
  );

  const videoPlayerEvent = (videoId: string, e: any) => {
    addVideoLog.mutate({
      type: e.type,
      timeStamp: e.type === "seeking" ? currentVideoTime : e.target.currentTime,
      videoId,
    });
  };

  const savePauseEvent = () => {
    addVideoLog.mutate({
      type: "pause",
      timeStamp: currentVideoTime,
      videoId: selectedVideo?.id,
    });
  };

  const hackUpdate = (video: any) => {
    setSelectedVideo(null);
    setTimeout(() => setSelectedVideo(video), 0);
  };

  const selectVideo = (playlistIndex: number, videoIndex: number) => {
    const pl = treatmentDetails?.data?.playlists?.[playlistIndex];
    const item = pl?.playlist_items?.[videoIndex];
    if (!pl?.status || !item?.status) return;

    setSelectedVideoPlayListIndex(playlistIndex);
    setSelectedVideoIndex(videoIndex);
    hackUpdate(item);
  };

  const playNextVideo = () => {
    const pls = treatmentDetails?.data?.playlists || [];
    if (!pls.length) return;

    let pIndex = selectedVideoPlayListIndex;
    const items = pls[pIndex]?.playlist_items || [];

    // next video in same playlist
    for (let i = selectedVideoIndex + 1; i < items.length; i++) {
      if (items[i]?.status) {
        selectVideo(pIndex, i);
        return;
      }
    }

    // next active playlist
    for (let pi = pIndex + 1; pi < pls.length; pi++) {
      if (!pls[pi]?.status) continue;
      const it = pls[pi]?.playlist_items || [];
      const firstActive = it.findIndex((v: any) => v?.status);
      if (firstActive !== -1) {
        selectVideo(pi, firstActive);
        return;
      }
    }
  };

  const onReady = () => {
    const yt = playerRef.current?.getInternalPlayer("youtube");
    ytRef.current = yt;

    try {
      yt?.setOption?.("captions", "track", { languageCode: captionLang });
    } catch {}

    const pending = seekAfterReloadRef.current;
    if (typeof pending === "number") {
      playerRef.current?.seekTo(pending, "seconds");
      seekAfterReloadRef.current = null;
      if (wasPlayingRef.current) yt?.playVideo?.();
    }
  };

  const forceReloadWithLang = (code: string) => {
    seekAfterReloadRef.current = currentVideoTime || 0;
    try {
      const state = ytRef.current?.getPlayerState?.();
      wasPlayingRef.current = state === 1;
    } catch {
      wasPlayingRef.current = false;
    }
    setRemountKey(`${selectedVideo?.id ?? "vid"}-${code}-${Date.now()}`);
  };

  const handleCaptionChange = (e: any) => {
    const code = e.target.value as string;
    setCaptionLang(code);
    forceReloadWithLang(code);
  };

  // ----------------------------- Final Submit Sign -----------------------------
  const sign = useMutation(
    async () => {
      const uniqueMandatory = Array.from(new Set(mandatoryButtonIds));
      const signedIds = signtureUpdates.map((x) => x.id);

      const missing = uniqueMandatory.filter((id) => !signedIds.includes(id));
      if (uniqueMandatory.length > 0 && missing.length > 0) {
        setShowBanner(true);
        window.scrollTo({ top: 0, behavior: "smooth" });
        throw new Error("Missing mandatory signatures");
      }

      setShowBanner(false);

      const sig = savedSignatureRef.current;
      if (!sig) {
        openSignatureModal(null);
        throw new Error("No saved signature yet");
      }

      const primaryImage = sig.image ?? null;
      const primaryName =
        sig.signed_party === "parent" || sig.signed_party === "guardian"
          ? sig.guardianName || sig.name || null
          : sig.name || null;

      const formattedData = {
        signed_party: sig.signed_party,
        name:
          sig.signed_party === "parent" || sig.signed_party === "guardian"
            ? sig.guardianName || primaryName || guardianName || ""
            : primaryName || "",
        image: primaryImage,
        multiple_signature_data: JSON.stringify(
          signtureUpdates.map((u) => ({
            id: u.id,
            timestamp: u.signature.timestamp || Date.now(),
          }))
        ),
      };

      const res = await axios.post(
        `/consent_bundle/${treatmentDetails.data.id}/upload_signature`,
        formattedData
      );
      return res.data;
    },
    {
      onSuccess: async () => {
        setIsFormSuccess(true);
        toast.success("Signed successfully");
        queryClient.invalidateQueries(["treatmentDetails", consentId]);

        if (!requirefp17dc) {
          await handleFinishClick();
        }
      },
      onError: (err: any) => {
        if (String(err?.message || "").includes("Missing mandatory")) return;
        if (String(err?.message || "").includes("No saved signature yet")) return;
        console.error(err);
        toast.error("Failed to sign");
      },
    }
  );

  const saveCapturedSignatureOnce = () => {
    if (
      (consentType === "parent" || consentType === "guardian") &&
      !guardianName.trim()
    ) {
      toast.error("Guardian/Parent full name is required");
      return;
    }

    const image = mySignature.current ? mySignature.current.toDataURL() : null;

    const sig: SavedSignature = {
      image: image && image !== "" ? image : null,
      name: null,
      signed_party: consentType,
      guardianName:
        consentType === "parent" || consentType === "guardian"
          ? guardianName.trim()
          : null,
      created_at: Date.now(),
    };

    setSavedSignature(sig);

    if (clickedButtonId) {
      setTimeout(() => applySavedSignatureToPlaceholder(clickedButtonId), 0);
    }

    closeSignatureModal();
  };

  const saveTypedNameOnce = () => {
    if (
      (consentType === "parent" || consentType === "guardian") &&
      !guardianName.trim()
    ) {
      toast.error("Guardian/Parent full name is required");
      return;
    }

    if (!userFullName.trim() && consentType === "patient") {
      toast.error("Full name is required");
      return;
    }

    const sig: SavedSignature = {
      image: null,
      name: consentType === "patient" ? userFullName.trim() : null,
      signed_party: consentType,
      guardianName:
        consentType === "parent" || consentType === "guardian"
          ? guardianName.trim()
          : null,
      created_at: Date.now(),
    };

    setSavedSignature(sig);

    if (clickedButtonId) {
      setTimeout(() => applySavedSignatureToPlaceholder(clickedButtonId), 0);
    }

    closeSignatureModal();
  };

  const handleMainSignClick = () => {
    if (!savedSignatureRef.current) {
      openSignatureModal(null);
      return;
    }
    sign.mutate();
  };

  const handleFinishClick = async () => {
    try {
      await axios.put(
        `/patient/consent_bundle/${treatmentDetails.data.id}/complete_consent_bundle`
      );
      navigate(`/${treatmentDetails.data.id}/thankyou`);
    } catch (error) {
      console.error("Error completing consent bundle:", error);
    }
  };

  const PdfViewer = () => {
    const rawContentState = convertToRaw(editorState.getCurrentContent());
    const consentForm = draftToHtml(rawContentState);
    setIsLoadingViewPdf(true);
    axios({
      url: `${process.env.REACT_APP_BASE_URL}consent_bundle/${treatmentDetails.data.id}/generate_fp17dc_form_pdf`,
      method: "GET",
      responseType: "blob",
      data: { consentForm },
    }).then((response) => {
      const href = URL.createObjectURL(response.data);
      setPdfUrl(href);
      setIsLoadingViewPdf(false);
    });
  };

  const handleNhsTreatmentCheckbox = async () => {
    if (!nhsChecked) {
      try {
        await axios.put(
          `/consent_bundle/${treatmentDetails.data.id}/mark_consent_bundle_as_signed_for_fp17dc_nhs_treatment`
        );
        setNhsChecked(true);
        setIsNhsTreatmentSigned(true);
      } catch (error) {
        console.error("Error marking NHS treatment as signed:", error);
      }
    }
  };

  const handlePrvTreatmentCheckbox = async () => {
    if (!prvChecked) {
      try {
        await axios.put(
          `/consent_bundle/${treatmentDetails.data.id}/mark_consent_bundle_as_signed_for_fp17dc_private_treatment`
        );
        setPrvChecked(true);
      } catch (error) {
        console.error("Error marking Private treatment as signed:", error);
      }
    }
  };

const cleanHtmlSpacing = (html: string) => {
  return html
    // remove trailing spaces/tabs at end of each line
    .replace(/[ \t]+$/gm, "")
    // remove leading spaces/tabs on fully blank lines
    .replace(/^[ \t]+$/gm, "")
    // collapse 3+ blank lines into a single blank line
    .replace(/\n\s*\n\s*\n+/g, "\n\n")
    // trim start/end of whole string
    .trim();
};

  // ----------------------------- Render -----------------------------
  return (
    <div className={styles.consentContainer}>
      {showBanner && (
        <div
          style={{
            backgroundColor: "red",
            color: "white",
            padding: "10px",
            textAlign: "center",
            fontWeight: "bold",
            marginBottom: "15px",
          }}
        >
          Please sign all mandatory buttons before proceeding.
        </div>
      )}

      {openAuthenticate ? (
        <Authenticate
          openAuthenticate={openAuthenticate}
          setOpenAuthenticate={setOpenAuthenticate}
          consentId={consentId}
        />
      ) : (
        <>
          <Box sx={{ width: "100%" }}>
            <Stepper activeStep={activeStep} alternativeLabel>
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
          </Box>

          {/* Step 0 - Consent Details */}
         {activeStep === 0 && (
  <div className={styles.consentSection}>
    <div className={styles.stepContent}>
      <Box mb={3}>
        <div className={styles.banner}>
          <img src={logo} alt="Logo" className={styles.bannerLogo} />
          <p className={styles.bannerText}>
            Welcome to Medicube,
            <br />
            where your dentist has curated trusted content for your review.
          </p>
        </div>
      </Box>

      <div className={styles.detailsCenter}>
        <h2 className={styles.detailsTitle}>Consent Details</h2>

        {treatmentDetails?.data?.patient && (
          <div className={styles.detailsCard}>
            <div className={styles.detailsGrid}>
              <div className={styles.detailRow}>
                <span className={styles.detailLabel}>Name</span>
                <span className={styles.detailValue}>
                  {[
                    treatmentDetails.data.patient.title,
                    treatmentDetails.data.patient.first_name,
                    treatmentDetails.data.patient.last_name,
                  ]
                    .filter(Boolean)
                    .join(" ") || "—"}
                </span>
              </div>

              <div className={styles.detailRow}>
                <span className={styles.detailLabel}>Email</span>
                <span className={styles.detailValue}>
                  {treatmentDetails.data.patient.email_address || "—"}
                </span>
              </div>

              <div className={styles.detailRow}>
                <span className={styles.detailLabel}>Mobile</span>
                <span className={styles.detailValue}>
                  {treatmentDetails.data.patient.mobile_phone || "—"}
                </span>
              </div>

              <div className={styles.detailRow}>
                <span className={styles.detailLabel}>Phone</span>
                <span className={styles.detailValue}>
                  {treatmentDetails.data.patient.home_phone || "—"}
                </span>
              </div>

              <div className={styles.detailRow}>
                <span className={styles.detailLabel}>Address</span>
                <span className={styles.detailValue}>
                  {[
                    treatmentDetails.data.patient.address_line_1,
                    treatmentDetails.data.patient.address_line_2,
                  ]
                    .filter(Boolean)
                    .join(", ") || "—"}
                </span>
              </div>

              <div className={styles.detailRow}>
                <span className={styles.detailLabel}>NHS Number</span>
                <span className={styles.detailValue}>
                  {treatmentDetails.data.patient.nhs_number || "—"}
                </span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>

    <div className={styles.footer}>
      <Button variant="outlined" color="secondary" disabled>
        Back
      </Button>
      <Button
        variant="contained"
        size="large"
        onClick={() => setActiveStep(1)}
      >
        Next
      </Button>
    </div>
  </div>
)}

          {activeStep === 1 && (
            <div className={styles.consentSection}>
              <div className={styles.row}>
                <div className={styles.column}>
                  <div className={styles.consentForm}>
                    {consentBody
                      ? parse(consentBody)
                      : "Consent form body not available."}
                  </div>

                 {hasActiveVideos ? (
  <div style={{ marginTop: 76 }}>
       <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        gap: 14,
        flexWrap: "wrap",
        marginBottom: 20,
      }}
    >
      <Typography
        variant="h5"
        sx={{
          fontWeight: 800,
          color: "#0f172a",
          lineHeight: 1.2,
        }}
      >
        Recommended Videos
      </Typography>

      <div
        style={{
          padding: "10px 18px",
          borderRadius: 14,
          background: "#e7f8ee",
          border: "1px solid #9fe3b7",
          color: "#047857",
          fontWeight: 800,
          fontSize: 15,
          whiteSpace: "nowrap",
        }}
      >
        {patientTime.watch} minutes watch time
      </div>
    </div>
    <div
      style={{
        border: "1px solid #f0b36d",
        background: "#f8f2ea",
        borderRadius: 20,
        padding: "18px 18px 16px",
        marginBottom: 22,
      }}
    >
      
      <div
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 8,
          background: "#e6ddd2",
          color: "#1f2937",
          fontWeight: 800,
          fontSize: 14,
          padding: "6px 10px",
          borderRadius: 8,
          marginBottom: 12,
        }}
      >

        
        <span style={{ color: "#f59e0b", fontSize: 18, lineHeight: 1 }}>⚠️</span>
        <span>Important Notice</span>
      </div>

      <div
        style={{
          fontSize: 15,
          lineHeight: 1.65,
          color: "#1f2937",
        }}
      >
        You must watch the complete educational video before providing consent.
        This ensures you have received comprehensive information about your
        treatment. We recommend that you watch the complete video to get the
        best out of your care.
      </div>
    </div>

    <div
      style={{
        display: "grid",
        gridTemplateColumns: isMobile ? "1fr" : "380px 1fr",
        gap: 16,
        alignItems: "start",
      }}
    >
      <div>
        <Typography
          variant="h6"
          sx={{ fontWeight: 800, mb: 1 }}
        >
          Video Playlist
        </Typography>

        {(treatmentDetails?.data?.playlists || [])
          .map((pl: any, plIndex: number) => {
            if (!pl?.status) return null;

            const activeItems = (pl?.playlist_items || []).filter(
              (x: any) => x?.status
            );
            if (!activeItems.length) return null;

            return (
              <Accordion
                key={pl?.id ?? plIndex}
                expanded={expandedPanel === plIndex}
                onChange={() =>
                  setExpandedPanel((p) =>
                    p === plIndex ? null : plIndex
                  )
                }
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography sx={{ fontWeight: 700 }}>
                    {pl?.title || pl?.name || `Playlist ${plIndex + 1}`}
                  </Typography>
                </AccordionSummary>

                <AccordionDetails>
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {(pl?.playlist_items || []).map((v: any, vIndex: number) => {
                      if (!v?.status) return null;

                      const isActive =
                        plIndex === selectedVideoPlayListIndex &&
                        vIndex === selectedVideoIndex;

                      return (
                        <button
                          key={v?.id ?? vIndex}
                          onClick={() => selectVideo(plIndex, vIndex)}
                          style={{
                            display: "flex",
                            gap: 10,
                            alignItems: "center",
                            textAlign: "left",
                            padding: "10px 12px",
                            borderRadius: 12,
                            border: "1px solid #e5e7eb",
                            background: isActive ? "#111827" : "white",
                            color: isActive ? "white" : "#111827",
                            cursor: "pointer",
                            fontWeight: 700,
                            width: "100%",
                          }}
                        >
                          <div
                            style={{
                              width: 84,
                              height: 52,
                              borderRadius: 10,
                              overflow: "hidden",
                              background: "#f3f4f6",
                              flexShrink: 0,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              border: isActive ? "1px solid #111827" : "1px solid #e5e7eb",
                            }}
                          >
                            {(v?.thumbnail_url ||
                              v?.thumbnail ||
                              v?.thumb ||
                              v?.image ||
                              v?.poster) ? (
                              <img
                                src={
                                  v?.thumbnail_url ||
                                  v?.thumbnail ||
                                  v?.thumb ||
                                  v?.image ||
                                  v?.poster
                                }
                                alt="thumbnail"
                                style={{ width: "100%", height: "100%", objectFit: "cover" }}
                              />
                            ) : (
                              <span style={{ fontSize: 12, opacity: 0.7 }}>No thumbnail</span>
                            )}
                          </div>

                          <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                            <div style={{ fontWeight: 800, lineHeight: 1.2 }}>
                              {v?.title || v?.name || v?.video_title || `Video ${vIndex + 1}`}
                            </div>
                            <div style={{ fontSize: 12, opacity: 0.75, fontWeight: 600 }}>
                              {isActive ? "Playing" : "Tap to play"}
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </AccordionDetails>
              </Accordion>
            );
          })}
      </div>

              <div>
                <Typography sx={{ fontWeight: 700, mb: 1 }}>
                  {selectedVideo?.title ||
                    selectedVideo?.name ||
                    selectedVideo?.video_title ||
                    "—"}
                </Typography>

                {selectedVideo ? (
                  <>
                    <ReactPlayer
                      key={remountKey}
                      ref={playerRef}
                      url={selectedVideo?.url}
                      controls
                      playing={false}
                      onReady={onReady}
                      config={{
                        youtube: {
                          playerVars: {
                            cc_load_policy: 1,
                            cc_lang_pref: captionLang,
                          },
                        },
                      }}
                      onPause={() =>
                        videoPlayerEvent(selectedVideo?.id, {
                          type: "pause",
                          target: { currentTime: currentVideoTime },
                        })
                      }
                      onPlay={() =>
                        videoPlayerEvent(selectedVideo?.id, {
                          type: "play",
                          target: { currentTime: currentVideoTime },
                        })
                      }
                      onSeek={(seekTime) =>
                        videoPlayerEvent(selectedVideo?.id, {
                          type: "seeking",
                          target: { currentTime: seekTime },
                        })
                      }
                      onEnded={() => {
                        playNextVideo();
                        videoPlayerEvent(selectedVideo?.id, {
                          type: "ended",
                          target: { currentTime: currentVideoTime },
                        });
                      }}
                      onProgress={({ playedSeconds }) =>
                        setCurrentVideoTime(playedSeconds)
                      }
                      width="100%"
                      height={isMobile ? "240px" : "480px"}
                    />

                    <Box mt={2} ml={1} display="flex" gap={2} flexWrap="wrap">
                      <Button
                        variant="contained"
                        color="info"
                        startIcon={<SkipNextIcon />}
                        onClick={() => playNextVideo()}
                      >
                        Skip Video
                      </Button>

                      <Button
                        variant="contained"
                        color="error"
                        startIcon={<ReportIcon />}
                        onClick={() => toast.info("Reported (demo)")}
                      >
                        Report
                      </Button>
                    </Box>
                  </>
                ) : (
                  <div style={{ padding: 12, border: "1px dashed #d1d5db", borderRadius: 12 }}>
                    No active video selected.
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : null}

                  <br />

                  <div className={styles.signBottomCenter}>
                    <FormControlLabel
                      className={styles.signBottomCheck}
                      control={
                        <Checkbox
                          checked={checked}
                          onChange={() => setChecked((val) => !val)}
                        />
                      }
                      label="I agree to sign this document electronically"
                    />

                    <Button
                      variant="contained"
                      color={isFormSuccess ? "success" : "warning"}
                      size="large"
                      onClick={handleMainSignClick}
                      disabled={sign.isLoading || !checked}
                    >
                      {isFormSuccess ? "Signed" : "Sign"}
                    </Button>
                  </div>
                </div>
              </div>

              {/* Signature Modal */}
              <Modal
                open={isModalOpen}
                onClose={closeSignatureModal}
                aria-labelledby="modal-signature"
              >
                <Box
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    width: { xs: "90%", sm: "600px" },
                    bgcolor: "background.paper",
                    boxShadow: 24,
                    p: 4,
                    borderRadius: "8px",
                    overflow: "hidden",
                  }}
                >
                  <h2 id="modal-signature">Sign Consent</h2>

                  <Box
                    sx={{
                      border: "2px solid black",
                      padding: "16px",
                      borderRadius: "8px",
                      width: "95%",
                      marginBottom: "20px",
                      marginLeft: "0",
                    }}
                  >
                    <FormControl component="fieldset" sx={{ mb: 1 }}>
                      <FormLabel component="legend" sx={{ fontWeight: "bold" }}>
                        Guardians to sign consent form on behalf of patient
                      </FormLabel>

                      <RadioGroup
                        value={consentType}
                        onChange={(e) => setConsentType(e.target.value as any)}
                      >
                        <FormControlLabel value="patient" control={<Radio />} label="I am Patient" />
                        <FormControlLabel value="parent" control={<Radio />} label="I am Parent" />
                        <FormControlLabel value="guardian" control={<Radio />} label="I am Guardian" />
                      </RadioGroup>

                      {(consentType === "parent" || consentType === "guardian") && (
                        <TextField
                          label="Full Name"
                          variant="outlined"
                          fullWidth
                          sx={{ mt: 2 }}
                          value={guardianName}
                          onChange={(e) => setGuardianName(e.target.value)}
                          required
                          error={!guardianName.trim()}
                          helperText={!guardianName.trim() ? "Full name is required" : ""}
                        />
                      )}
                    </FormControl>
                  </Box>

                  {!isMobile && (
                    <>
                      <Chip
                        label="Signature"
                        variant={activeInput === "signature" ? "filled" : "outlined"}
                        onClick={() => setActiveInput("signature")}
                        sx={{
                          mr: 2,
                          fontSize: "1.2rem",
                          padding: "10px 20px",
                          backgroundColor: activeInput === "signature" ? "#FFA500" : "transparent",
                          color: activeInput === "signature" ? "white" : "inherit",
                        }}
                      />
                      <Chip
                        label="Enter Name"
                        variant={activeInput === "name" ? "filled" : "outlined"}
                        onClick={() => setActiveInput("name")}
                        sx={{
                          fontSize: "1.2rem",
                          padding: "10px 20px",
                          backgroundColor: activeInput === "name" ? "#FFA500" : "transparent",
                          color: activeInput === "name" ? "white" : "inherit",
                        }}
                      />
                    </>
                  )}

                  {activeInput === "signature" && (
                    <div
                      className={styles.signPad}
                      style={{
                        width: "100%",
                        border: "2px solid #243474",
                        margin: "10px 0",
                        padding: "10px",
                        borderRadius: "8px",
                        boxSizing: "border-box",
                        overflow: "hidden",
                        position: "relative",
                      }}
                    >
                      <SignatureCanvas
                        penColor="#243474"
                        ref={mySignature}
                        canvasProps={{
                          className: styles.signCanvas,
                          width: isMobile ? Math.min(window.innerWidth * 0.9, 400) : 600,
                          height: isMobile ? Math.min(window.innerWidth * 0.9 * 0.5, 300) : 400,
                        }}
                        onBegin={() => {
                          setIsSignatureCaptured(true);
                          setIsImageVisible(false);
                        }}
                      />

                      {isImageVisible && (
                        <img
                          src={signatureImage}
                          alt="Draw Here"
                          style={{
                            position: "absolute",
                            top: "50%",
                            left: "50%",
                            transform: "translate(-50%, -50%)",
                            width: "50%",
                            maxWidth: "200px",
                            height: "auto",
                            opacity: 0.5,
                            pointerEvents: "none",
                          }}
                        />
                      )}
                    </div>
                  )}

                  {activeInput === "name" && (
                    <TextField
                      label="Full Name"
                      variant="outlined"
                      fullWidth
                      value={userFullName}
                      onChange={(e) => setUserFullName(e.target.value)}
                      sx={{ mt: 2 }}
                    />
                  )}

                  <div style={{ display: "flex", justifyContent: "space-between", marginTop: "10px" }}>
                    <Button variant="outlined" color="error" onClick={closeSignatureModal}>
                      Close
                    </Button>

                    <div style={{ display: "flex", alignItems: "center" }}>
                      {activeInput === "signature" && (
                        <>
                          <IconButton aria-label="erase" onClick={eraseSignature}>
                            <DeleteIcon />
                          </IconButton>
                          <Button
                            variant="contained"
                            color="success"
                            size="large"
                            onClick={saveCapturedSignatureOnce}
                            disabled={!isSignatureCaptured}
                            sx={{ ml: 2 }}
                          >
                            Save Signature
                          </Button>
                        </>
                      )}

                      {activeInput === "name" && (
                        <Button
                          variant="contained"
                          color="success"
                          size="large"
                          onClick={saveTypedNameOnce}
                          disabled={consentType === "patient" ? !userFullName.trim() : false}
                          sx={{ ml: 2 }}
                        >
                          Save Name
                        </Button>
                      )}
                    </div>
                  </div>
                </Box>
              </Modal>

              <div className={styles.footer}>
                <Button
                  variant="outlined"
                  color="secondary"
                  size="large"
                  onClick={() => setActiveStep(0)}
                >
                  Back
                </Button>

                {isFormSuccess &&
                  (requirefp17dc ? (
                    <Button
                      variant="contained"
                      size="large"
                      onClick={() => {
                        PdfViewer();
                        savePauseEvent();
                        setActiveStep(2); 
                      }}
                    >
                      Next
                    </Button>
                  ) : (
                    <Button variant="contained" size="large" onClick={handleFinishClick}>
                      Finish
                    </Button>
                  ))}
              </div>
            </div>
          )}

          {/* Step 2 - FP17DC */}
          {activeStep === 2 && (
            <div className={styles.consentSection}>
              <div className={styles.fp17dcWrapper}>
                <div className={styles.fp17dcCard}>
                  <div className={styles.fp17dcHeader}>
                    <h2 className={styles.header}>FP17DC Form</h2>
                    <p className={styles.fp17dcSubtext}>
                      Review the form and apply the required signatures before finishing.
                    </p>
                  </div>

                  {isLoadingViewPdf && (
                    <div className={styles.loaderWrapper}>
                      <CircularProgress />
                      <p>Loading FP17DC form...</p>
                    </div>
                  )}

                  {pdfUrl && (
                    <>
                     <div className={styles.pdfViewerWrapper}>
                      {isMobile ? (
                        <div className={styles.mobilePdfCard}>
                          <div className={styles.mobilePdfPreview}>
                            <iframe
                              src={`${pdfUrl}#toolbar=0&view=FitH`}
                              className={styles.mobilePdfFrame}
                              title="PDF Preview"
                            />
                          </div>

                          <div className={styles.mobilePdfActions}>
                            <a
                              href={pdfUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className={styles.openPdfBtn}
                            >
                              Open Full PDF
                            </a>
                          </div>
                        </div>
                      ) : (
                        <div className={styles.desktopPdfOuter}>
                          <iframe
                            src={`${pdfUrl}#toolbar=0`}
                            className={styles.desktopPdfViewer}
                            title="PDF Viewer"
                            scrolling="yes"
                          />
                        </div>
                      )}
</div>

                      <div className={styles.signatureSection}>
                        <div className={styles.signatureGrid}>
                          <label className={styles.signatureLabel}>
                            <input
                              type="checkbox"
                              checked={nhsChecked}
                              onChange={handleNhsTreatmentCheckbox}
                              className={styles.hiddenCheckbox}
                            />
                            <button
                              type="button"
                              className={`${styles.signatureButton} ${
                                nhsChecked ? styles.signed : styles.pending
                              }`}
                              onClick={handleNhsTreatmentCheckbox}
                            >
                              <span className={styles.signatureButtonTitle}>
                                {nhsChecked ? "NHS Signed" : "Apply Signature for NHS"}
                              </span>
                            </button>
                          </label>

                          <label className={styles.signatureLabel}>
                            <input
                              type="checkbox"
                              checked={prvChecked}
                              onChange={handlePrvTreatmentCheckbox}
                              className={styles.hiddenCheckbox}
                            />
                            <button
                              type="button"
                              className={`${styles.signatureButton} ${
                                prvChecked ? styles.signed : styles.pending
                              }`}
                              onClick={handlePrvTreatmentCheckbox}
                            >
                              <span className={styles.signatureButtonTitle}>
                                {prvChecked ? "Private Signed" : "Apply Signature for Private"}
                              </span>
                            </button>
                          </label>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>

              <div className={styles.footer}>
                <Button
                  variant="outlined"
                  color="secondary"
                  size="large"
                  onClick={() => setActiveStep(1)}
                  className={styles.footerButton}
                >
                  Back
                </Button>

                <Button
                  variant="contained"
                  size="large"
                  onClick={handleFinishClick}
                  disabled={!isNhsTreatmentSigned}
                  className={`${styles.footerButton} ${styles.finishButton} ${
                    !isNhsTreatmentSigned ? styles.finishButtonDisabled : ""
                  }`}
                >
                  Finish
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}